// CommandProvider — runs an external shell command and parses stdout as ExternalInsight.

import { execFile } from "node:child_process";
import { promisify } from "node:util";

import {
  ExternalInsightSchema,
  type ExternalInsight,
  type InsightProvider,
  type InsightQuery,
} from "./types.js";

const execFileAsync = promisify(execFile);

const DEFAULT_TIMEOUT_MS = 10_000;
const MAX_BUFFER_BYTES = 10 * 1024 * 1024;

export interface CommandProviderConfig {
  name: string;
  /** Shell command (pipes allowed when executed with `shell: true`). */
  command: string;
  timeout?: number;
  env?: Record<string, string>;
  /** Working directory override; defaults to `query.projectRoot`. */
  cwd?: string;
}

export class CommandProvider implements InsightProvider {
  readonly name: string;
  private readonly command: string;
  private readonly timeout: number;
  private readonly extraEnv: Record<string, string>;
  private readonly cwdOverride: string | undefined;

  constructor(config: CommandProviderConfig) {
    this.name = config.name;
    this.command = config.command;
    this.timeout = config.timeout ?? DEFAULT_TIMEOUT_MS;
    this.extraEnv = config.env ?? {};
    this.cwdOverride = config.cwd;
  }

  async provide(query: InsightQuery): Promise<ExternalInsight> {
    const cwd = this.cwdOverride ?? query.projectRoot;
    const env = {
      ...process.env,
      ...this.extraEnv,
      ANALYZER_CHANGED_FILES: JSON.stringify(query.changedFiles ?? []),
    };

    let stdout: string;
    try {
      const result = await execFileAsync(this.command, [], {
        shell: true,
        cwd,
        env,
        timeout: this.timeout,
        maxBuffer: MAX_BUFFER_BYTES,
      });
      stdout = String(result.stdout);
    } catch (err: unknown) {
      throw formatExecError(this.name, err);
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(stdout.trim());
    } catch {
      throw new CommandProviderError(
        this.name,
        `Failed to parse stdout as JSON: ${truncate(stdout)}`,
      );
    }

    const validated = ExternalInsightSchema.safeParse(parsed);
    if (!validated.success) {
      throw new CommandProviderError(
        this.name,
        `ExternalInsight validation failed: ${validated.error.message}`,
      );
    }

    return validated.data;
  }
}

export class CommandProviderError extends Error {
  readonly providerName: string;

  constructor(providerName: string, message: string) {
    super(`[${providerName}] ${message}`);
    this.name = "CommandProviderError";
    this.providerName = providerName;
  }
}

function formatExecError(providerName: string, err: unknown): CommandProviderError {
  if (err && typeof err === "object") {
    const e = err as { killed?: boolean; signal?: string; code?: number | string; message?: string };
    if (e.killed && e.signal === "SIGTERM") {
      return new CommandProviderError(providerName, "Command timed out");
    }
    if (e.code !== undefined && e.code !== 0) {
      return new CommandProviderError(
        providerName,
        `Command exited with code ${String(e.code)}`,
      );
    }
    if (e.message) {
      return new CommandProviderError(providerName, e.message);
    }
  }
  return new CommandProviderError(providerName, String(err));
}

function truncate(text: string, max = 200): string {
  const trimmed = text.trim();
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max)}…`;
}
